import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Menu,
  MenuItem,
  Paper,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import UpdateIcon from '@mui/icons-material/Update';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment-timezone';
import AnswerEditor, { draftFor, draftIsValid, draftToPayload } from './AnswerEditor';
import { scopeChips, scopeFromTours, useTagScope } from './tagScope';
import { CHANNEL_COPY } from './channelCopy';
import { ignoreReasons } from './knowledgeReviewApi';
import { allFromWebsite, answerDateRange, defaultTeamAnswerIndex, fromWebsite, timelineMessage } from './teamAnswers';
import DeleteButton from '../../components/DeleteButton';

export const KIND_META = {
  gap: { label: 'Unanswered question', short: 'Unanswered', icon: HelpOutlineIcon, color: 'primary' },
  conflict: { label: 'Answers don’t match', short: 'Conflict', icon: CompareArrowsIcon, color: 'warning' },
  stale: { label: 'Is this still right?', short: 'Check', icon: UpdateIcon, color: 'secondary' },
};

// A conflict is either knowledge base vs team, or team vs team (no KB excerpt).
export const isTeamConflict = (item) => item.kind === 'conflict' && !item.kb_excerpt;
export const kindLabel = (item) => {
  if (!isTeamConflict(item)) return KIND_META[item.kind].label;
  return allFromWebsite(item.team_answers) ? 'Your website gives different answers' : 'Your team gives different answers';
};

const fromNow = (ts) => (ts ? moment(ts).fromNow() : '');
const times = (n) => (n === 1 ? 'once' : `${n} times`);
const websiteLine = (a) => `On your website${a.pages ? ` · ${a.pages}` : ''}`;

/* ---------- shared pieces ---------- */

// Where each inbox lives in the dashboard menu.
const INBOX_PLACE = { support: 'AI Suite', widget: 'Web Chat Widget' };

// A trained answer used by both chatbots has its card in each inbox ("twins"). Ticked, this
// decision closes the other inbox's card too; Undo reopens it.
function useTwinChoice(item) {
  const [closeTwins, setCloseTwins] = useState(true);
  const hasTwin = !!item.twins?.length;
  return { hasTwin, closeTwins, setCloseTwins, arg: hasTwin ? { close_twins: closeTwins } : {} };
}

function TwinCheckbox({ item, twin, disabled }) {
  if (!twin.hasTwin) return null;
  return (
    <FormControlLabel
      control={(
        <Checkbox size="small" checked={twin.closeTwins} onChange={(e) => twin.setCloseTwins(e.target.checked)} disabled={disabled} />
      )}
      label={(
        <Typography variant="body2">
          Also close the matching card in your {INBOX_PLACE[item.twins[0].channel]} inbox. This answer is used by both, so a change here applies to both.
        </Typography>
      )}
    />
  );
}

function KindHeader({ item }) {
  const meta = KIND_META[item.kind];
  const Icon = meta.icon;
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <Chip icon={<Icon />} label={kindLabel(item)} color={meta.color} size="small" variant="outlined" />
      {item.ask_count > 0 && (
        <Typography variant="body2" color="text.secondary">
          Asked {times(item.ask_count)} · last {fromNow(item.last_asked_ts)}
        </Typography>
      )}
    </Stack>
  );
}

// `scope`: a saved answer (tags, exclusions; see tagScope.js), or { tours } for a card's products.
function ProductLine({ scope }) {
  const chips = scopeChips(scope, useTagScope());
  if (scope.all_tours) return <Typography variant="body2" color="text.secondary" title={chips[0].title}>{chips[0].label}</Typography>;
  if (!chips.length) return null;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {chips.map((c) => <Chip key={c.key} label={c.label} title={c.title} size="small" />)}
    </Stack>
  );
}

function Section({ title, icon, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
        {icon}
        <Typography variant="subtitle2">{title}</Typography>
      </Stack>
      {children}
    </Box>
  );
}

function Examples({ examples, heading }) {
  const [showAll, setShowAll] = useState(false);
  if (!examples?.length) return null;
  const shown = showAll ? examples : examples.slice(0, 3);
  return (
    <Section title={heading}>
      <Stack spacing={1}>
        {shown.map((ex, i) => (
          <Box key={i} sx={{ borderLeft: 3, borderColor: 'divider', pl: 1.5 }}>
            <Typography variant="body2">“{ex.text}”</Typography>
            <Typography variant="caption" color="text.secondary">
              {[ex.tour, ex.source, fromNow(ex.asked_ts)].filter(Boolean).join(' · ')}
              {ex.conversation_url && (
                <>
                  {' · '}
                  <Link href={ex.conversation_url} target="_blank" rel="noopener" underline="hover">
                    Open chat <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                  </Link>
                </>
              )}
            </Typography>
          </Box>
        ))}
      </Stack>
      {examples.length > 3 && (
        <Button size="small" onClick={() => setShowAll((s) => !s)} sx={{ mt: 0.5, px: 0 }}>
          {showAll ? 'Show fewer' : `Show all ${examples.length}`}
        </Button>
      )}
    </Section>
  );
}

// Selectable team answers. `selected` is an index into answers, or 'own'.
function TeamAnswerPicker({ answers, selected, onSelect, disabled, allowOwn = true }) {
  const option = (key, body) => (
    <Paper
      key={key}
      variant="outlined"
      onClick={() => !disabled && onSelect(key)}
      sx={{
        p: 1.25,
        borderRadius: '10px',
        cursor: disabled ? 'default' : 'pointer',
        borderColor: selected === key ? 'primary.main' : 'divider',
        bgcolor: selected === key ? 'action.selected' : 'transparent',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Radio size="small" checked={selected === key} disabled={disabled} sx={{ p: 0.25 }} inputProps={{ 'aria-label': typeof key === 'number' ? `Team answer ${key + 1}` : 'Write my own' }} />
        <Box sx={{ flex: 1 }}>{body}</Box>
      </Stack>
    </Paper>
  );
  return (
    <Stack spacing={1}>
      {answers.map((a, i) => option(i, (
        <>
          <Typography variant="body2">“{a.text}”</Typography>
          <Typography variant="caption" color="text.secondary">
            {fromWebsite(a)
              ? websiteLine(a)
              : <>Sent {times(a.count)}{a.agents?.length ? ` by ${a.agents.join(', ')}` : ''} · {answerDateRange(a) || `last ${fromNow(a.last_ts)}`}</>}
          </Typography>
        </>
      )))}
      {allowOwn && option('own', <Typography variant="body2">Write my own answer</Typography>)}
    </Stack>
  );
}

function IgnoreButton({ channel, onIgnore, disabled }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <>
      <Button color="inherit" disabled={disabled} onClick={(e) => setAnchor(e.currentTarget)} endIcon={<ArrowDropDownIcon />}>
        Ignore
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {ignoreReasons(channel).map((r) => (
          <MenuItem key={r.value} onClick={() => { setAnchor(null); onIgnore(r.value); }}>{r.label}</MenuItem>
        ))}
      </Menu>
    </>
  );
}

function SaveHint({ channel, autoReplyOn }) {
  return (
    <Typography variant="caption" color="text.secondary">
      {CHANNEL_COPY[channel].saveHint(autoReplyOn)}
    </Typography>
  );
}

function ActionBar({ children, hint }) {
  return (
    <Box
      sx={{
        // Sits at the end of the card, in the flow (it used to stick to the bottom of the
        // window; Jordan preferred it with its box, 2026-09-24).
        pt: 1.5,
        pb: { xs: 2, md: 2.5 },
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">{children}</Stack>
      {hint && <Box sx={{ mt: 0.5 }}>{hint}</Box>}
    </Box>
  );
}

// Did the team disagree, or did the answer change over time? (see teamAnswers.js)
function TeamTimelineHint({ answers }) {
  const msg = timelineMessage(answers);
  if (!msg) return null;
  return <Alert severity={msg.severity} sx={{ mb: 1 }}>{msg.text}</Alert>;
}

/* ---------- Unanswered question ---------- */

function GapDetail({ item, run, busy, canWrite, productOptions, canShare, autoReplyOn, api, onSkip }) {
  const team = item.team_answers || [];
  const copy = CHANNEL_COPY[item.channel];
  const [question, setQuestion] = useState(item.question);
  // Nothing pre-selected when the team's replies leave it unclear (see teamAnswers.js).
  const [picked, setPicked] = useState(() => defaultTeamAnswerIndex(team) ?? (team.length ? null : 'own'));
  const ctx = useTagScope();
  const [draft, setDraft] = useState(() => draftFor(item, team[defaultTeamAnswerIndex(team)]?.text || '', ctx));
  const disabled = !canWrite || !!busy;

  const pick = (key) => {
    setPicked(key);
    setDraft((d) => ({ ...d, answer: key === 'own' ? '' : team[key].text }));
  };

  return (
    <Stack spacing={2.5}>
      <KindHeader item={item} />
      <TextField
        label="Question"
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        fullWidth
        disabled={disabled}
        helperText={copy.questionHelper}
      />
      <ProductLine scope={{ tours: item.tours }} />

      {item.booking_specific_hint && (
        // A warning, not an action: one specific answer here (a pickup stop, a time) would be wrong
        // for many guests. Either train an answer that points guests to their own details, or
        // Ignore -> "It depends on each booking". Set by the mining job for pickup questions on
        // tours whose bookings give each guest their own pickup (knowledge_mining.flag_pickup_cards).
        <Alert severity="warning">{copy.bookingHint}</Alert>
      )}

      <Examples examples={item.examples} heading={CHANNEL_COPY[item.channel].askedHeading} />

      <Section title="How your team answered" icon={<GroupsOutlinedIcon fontSize="small" color="action" />}>
        {team.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nobody on your team has answered this yet, so the answer below is up to you.
          </Typography>
        ) : (
          <>
            <TeamTimelineHint answers={team} />
            <TeamAnswerPicker answers={team} selected={picked} onSelect={pick} disabled={disabled} />
          </>
        )}
      </Section>

      <Divider />
      <AnswerEditor
        question={question}
        draft={draft}
        onChange={setDraft}
        productOptions={productOptions}
        suggestedTours={item.tours}
        channel={item.channel}
        canShare={canShare}
        disabled={disabled}
        api={api}
        autoFocus={picked === 'own' && team.length > 0}
      />

      <ActionBar hint={canWrite && <SaveHint channel={item.channel} autoReplyOn={autoReplyOn} />}>
        <Button
          variant="contained"
          disabled={disabled || !question.trim() || !draftIsValid(draft)}
          onClick={() => run('approve', draftToPayload(question, draft))}
          startIcon={busy === 'approve' ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Train with this answer
        </Button>
        <IgnoreButton channel={item.channel} disabled={disabled} onIgnore={(reason) => run('ignore', { reason })} />
        <Box sx={{ flex: 1 }} />
        <Button color="inherit" onClick={onSkip} disabled={!!busy}>Skip for now</Button>
      </ActionBar>
    </Stack>
  );
}

/* ---------- Conflict ---------- */

const CONFLICT_CHOICES = [
  { value: 'team', label: 'My team is right. Use their answer.' },
  { value: 'kb', label: 'The knowledge base is right.' },
  { value: 'product', label: 'It depends on the product.' },
  { value: 'own', label: 'Neither. I’ll write the answer.' },
];
// Team vs team: no knowledge base side to keep.
const TEAM_CONFLICT_CHOICES = [
  { value: 'team', label: 'One of these is right.' },
  { value: 'product', label: 'It depends on the product.' },
  { value: 'own', label: 'Neither. I’ll write the answer.' },
];

function ConflictDetail({ item, run, busy, canWrite, productOptions, canShare, autoReplyOn, api, onSkip }) {
  const team = item.team_answers || [];
  const teamOnly = isTeamConflict(item);
  let choices = teamOnly ? TEAM_CONFLICT_CHOICES : CONFLICT_CHOICES;
  if (!teamOnly && allFromWebsite(team)) {
    choices = choices.map((c) => (c.value === 'team' ? { ...c, label: 'My website is right. Use its answer.' } : c));
  }
  const [choice, setChoice] = useState('team');
  const [picked, setPicked] = useState(() => defaultTeamAnswerIndex(team));
  const ctx = useTagScope();
  const [draft, setDraft] = useState(() => draftFor(item, team[defaultTeamAnswerIndex(team)]?.text || '', ctx));
  // One answer per version the team gave, scoped to the products (or their collections) it was given for.
  const [productDrafts, setProductDrafts] = useState(() => team.map((a) => ({
    ...draftFor(item, a.text, ctx), all_tours: false, ...scopeFromTours(a.tours || [], ctx),
  })));
  const disabled = !canWrite || !!busy;

  const choose = (value) => {
    setChoice(value);
    if (value === 'team') setDraft((d) => ({ ...d, answer: team[picked]?.text || '' }));
    if (value === 'own') setDraft((d) => ({ ...d, answer: '' }));
  };
  const pickTeam = (key) => {
    setPicked(key);
    setDraft((d) => ({ ...d, answer: team[key].text }));
  };

  // Knowledge-base conflicts only: stop the bot using the contradicting passage.
  const [hidePassage, setHidePassage] = useState(true);
  const suppress = teamOnly ? {} : { suppress_kb_excerpt: hidePassage };
  const twin = useTwinChoice(item);
  const save = () => {
    if (choice === 'kb') return run('keep', twin.arg);
    if (choice === 'product') {
      return run('approve', { answers: productDrafts.map((d) => draftToPayload(item.question, d)), ...suppress, ...twin.arg });
    }
    return run('approve', { ...draftToPayload(item.question, draft), ...suppress, ...twin.arg });
  };
  const canSave = choice === 'kb'
    || (choice === 'product' ? productDrafts.length > 0 && productDrafts.every(draftIsValid) : draftIsValid(draft));

  return (
    <Stack spacing={2.5}>
      <KindHeader item={item} />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>{item.question}</Typography>

      {!teamOnly && (
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '12px', flex: 1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
            <DescriptionOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">Your knowledge base says</Typography>
          </Stack>
          <Typography variant="body2">“{item.kb_excerpt?.text}”</Typography>
          <Typography variant="caption" color="text.secondary">From {item.kb_excerpt?.doc_name}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '12px', flex: 1, borderColor: 'warning.light' }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
            <GroupsOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">{allFromWebsite(team) ? 'Your website says' : 'Your team has been saying'}</Typography>
          </Stack>
          <Stack spacing={1}>
            {team.map((a, i) => (
              <Box key={i}>
                <Typography variant="body2">“{a.text}”</Typography>
                <Typography variant="caption" color="text.secondary">
                  {fromWebsite(a) ? websiteLine(a) : (
                    <>
                      {times(a.count)}{item.team_since_ts ? ` since ${moment(item.team_since_ts).format('MMM D')}` : ''}
                      {a.agents?.length ? ` · ${a.agents.join(', ')}` : ''}
                    </>
                  )}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Paper>
      </Stack>
      )}

      {team.length > 1 && <TeamTimelineHint answers={team} />}

      <Examples examples={item.examples} heading={CHANNEL_COPY[item.channel].askedHeading} />

      <Section title={teamOnly ? 'Which answer is right?' : 'Which is correct?'}>
        <Stack spacing={0.5}>
          {choices.map((c) => (
            <Stack
              key={c.value}
              direction="row"
              alignItems="center"
              onClick={() => !disabled && choose(c.value)}
              sx={{ cursor: disabled ? 'default' : 'pointer' }}
            >
              <Radio size="small" checked={choice === c.value} disabled={disabled} inputProps={{ 'aria-label': c.label }} />
              <Typography variant="body2">{c.label}</Typography>
            </Stack>
          ))}
        </Stack>
      </Section>

      {choice === 'team' && team.length > 1 && (
        <TeamAnswerPicker answers={team} selected={picked} onSelect={pickTeam} disabled={disabled} allowOwn={false} />
      )}

      {(choice === 'team' || choice === 'own') && (
        <AnswerEditor
          question={item.question}
          draft={draft}
          onChange={setDraft}
          productOptions={productOptions}
          suggestedTours={item.tours}
          channel={item.channel}
          canShare={canShare}
          disabled={disabled}
          api={api}
          autoFocus={choice === 'own'}
        />
      )}

      {choice === 'product' && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {CHANNEL_COPY[item.channel].perProductHint}
          </Typography>
          {productDrafts.map((d, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2">Answer {i + 1}</Typography>
                {productDrafts.length > 1 && (
                  <Button size="small" color="inherit" disabled={disabled} onClick={() => setProductDrafts((ds) => ds.filter((_, j) => j !== i))}>
                    Remove
                  </Button>
                )}
              </Stack>
              <AnswerEditor
                question={item.question}
                draft={d}
                onChange={(nd) => setProductDrafts((ds) => ds.map((x, j) => (j === i ? nd : x)))}
                productOptions={productOptions}
                suggestedTours={item.tours}
                channel={item.channel}
                canShare={canShare}
                disabled={disabled}
                api={api}
              />
            </Paper>
          ))}
          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              disabled={disabled}
              onClick={() => setProductDrafts((ds) => [...ds, {
                ...draftFor(item, '', ctx), all_tours: false, tours: [], collections: [], access_tags: [], excluded_tours: [],
              }])}
            >
              Add another answer
            </Button>
          </Box>
        </Stack>
      )}

      {!teamOnly && choice !== 'kb' && item.kb_excerpt && (
        // The passage the card quoted stops reaching the bot (cut from the file's text when
        // the knowledge base is indexed); the stored file itself is untouched. On by default,
        // shown before training so nothing is hidden sight unseen.
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '12px' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={hidePassage} onChange={(e) => setHidePassage(e.target.checked)} disabled={disabled} />}
            label={<Typography variant="body2">Stop the chatbot using this passage from <b>{item.kb_excerpt.doc_name}</b></Typography>}
          />
          <Typography variant="body2" sx={{ ml: 4, fontStyle: 'italic', color: hidePassage ? 'text.primary' : 'text.disabled' }}>
            “{item.kb_excerpt.text}”
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ ml: 4, mt: 0.5 }}>
            {hidePassage
              ? 'Your file itself isn’t changed. Update it when you can, so your team reads the same thing.'
              : 'The chatbot will still see this passage, but your trained answer takes priority over it.'}
          </Typography>
        </Paper>
      )}

      <TwinCheckbox item={item} twin={twin} disabled={disabled} />

      <ActionBar hint={canWrite && choice !== 'kb' && <SaveHint channel={item.channel} autoReplyOn={autoReplyOn} />}>
        <Button
          variant="contained"
          disabled={disabled || !canSave}
          onClick={save}
          startIcon={busy === 'approve' || busy === 'keep' ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {choice === 'kb' ? 'Keep the knowledge base answer' : 'Train with this answer'}
        </Button>
        <IgnoreButton channel={item.channel} disabled={disabled} onIgnore={(reason) => run('ignore', { reason, ...twin.arg })} />
        <Box sx={{ flex: 1 }} />
        <Button color="inherit" onClick={onSkip} disabled={!!busy}>Skip for now</Button>
      </ActionBar>
    </Stack>
  );
}

/* ---------- Stale ---------- */

function StaleDetail({ item, run, busy, canWrite, productOptions, canShare, autoReplyOn, api, onSkip }) {
  const saved = item.answer;
  const [editing, setEditing] = useState(false);
  const [question, setQuestion] = useState(saved.question);
  const [draft, setDraft] = useState(() => draftFor(item));
  const disabled = !canWrite || !!busy;
  const twin = useTwinChoice(item);

  // Name who set the stop date (someone to ask), and the phrase it came from if suggested.
  const setBy = saved.valid_until_set_by || saved.approved_by;
  const reason = item.stale_reason === 'expired'
    ? `${setBy ? `${setBy} set` : 'Someone set'} this answer to stop on ${moment(saved.valid_until).format('MMM D, YYYY')}, so the bot isn’t using it right now.${saved.valid_until_reason ? ` The date came from “${saved.valid_until_reason}” in the answer.` : ''}`
    : `This answer was saved ${fromNow(saved.last_confirmed_ts)} and nobody has checked it since.`;

  return (
    <Stack spacing={2.5}>
      <KindHeader item={item} />
      <Alert severity="warning">{reason}</Alert>

      {!editing ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>{saved.question}</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1.5 }}>{saved.answer}</Typography>
          <ProductLine scope={saved} />
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            Saved by {saved.approved_by} · {moment(saved.approved_ts).format('MMM D, YYYY')}
          </Typography>
        </Paper>
      ) : (
        <>
          <TextField label="Question" value={question} onChange={(e) => setQuestion(e.target.value)} fullWidth disabled={disabled} />
          <AnswerEditor
            question={question}
            draft={draft}
            onChange={setDraft}
            productOptions={productOptions}
            suggestedTours={item.tours}
            channel={item.channel}
            canShare={canShare}
            disabled={disabled}
            api={api}
            autoFocus
          />
        </>
      )}

      <TwinCheckbox item={item} twin={twin} disabled={disabled} />

      <ActionBar hint={canWrite && editing && <SaveHint channel={item.channel} autoReplyOn={autoReplyOn} />}>
        {!editing ? (
          <>
            <Button
              variant="contained"
              disabled={disabled}
              onClick={() => run('keep', twin.arg)}
              startIcon={busy === 'keep' ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {item.stale_reason === 'expired' ? 'Still right. Keep using it.' : 'Yes, still right'}
            </Button>
            <Button variant="outlined" disabled={disabled} onClick={() => setEditing(true)}>Update it</Button>
            <DeleteButton disabled={disabled} onClick={() => run('removeStaleAnswer', twin.arg)}>Delete it</DeleteButton>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              disabled={disabled || !question.trim() || !draftIsValid(draft)}
              onClick={() => run('approve', { ...draftToPayload(question, draft), ...twin.arg })}
              startIcon={busy === 'approve' ? <CircularProgress size={16} color="inherit" /> : null}
            >
              Train with updated answer
            </Button>
            <Button color="inherit" disabled={!!busy} onClick={() => setEditing(false)}>Cancel</Button>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Button color="inherit" onClick={onSkip} disabled={!!busy}>Skip for now</Button>
      </ActionBar>
    </Stack>
  );
}

/* ---------- dispatcher ---------- */

const DETAIL_BY_KIND = { gap: GapDetail, conflict: ConflictDetail, stale: StaleDetail };

export default function ReviewDetail(props) {
  const { item } = props;
  const [busy, setBusy] = useState(null);
  useEffect(() => setBusy(null), [item.id]);
  // Resolving the last card in view closes this panel before the action settles.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const run = async (action, arg) => {
    setBusy(action);
    try {
      await props.onAction(item, action, arg);
    } finally {
      if (mounted.current) setBusy(null);
    }
  };

  const Detail = DETAIL_BY_KIND[item.kind];
  return (
    <Box>
      {!props.canWrite && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {CHANNEL_COPY[item.channel].readOnly}
        </Alert>
      )}
      {/* key: reset all local editor state when moving to another card */}
      <Detail key={item.id} {...props} run={run} busy={busy} />
    </Box>
  );
}
